// Bard Club キャラ診断の「しくみ」(どの言語でも共通)。
//
// 12問・各3択。Q1〜6 で「人前のあなた」、Q7〜12 で「ひとりのあなた」を
// 6つのタイプで判定し、6 × 6 = 36 通りに1人ずつ配役する。
// 問いや役の文章は言語ごとに src/shared/content/ にある。

export type TypeId = "king" | "lover" | "fool" | "schemer" | "warrior" | "dreamer";

export const typeOrder: TypeId[] = ["king", "lover", "fool", "schemer", "warrior", "dreamer"];

/**
 * 12問それぞれの選択肢 (A, B, C) が、どの2つのタイプに1点ずつ入るか。
 * どの問いでも、3つの選択肢で6タイプがちょうど1回ずつ出る
 */
export const QUESTION_TYPES: [TypeId, TypeId][][] = [
  // ----- Q1〜6: 人前のあなた -----
  [["king", "warrior"], ["fool", "lover"], ["schemer", "dreamer"]],
  [["king", "lover"], ["fool", "schemer"], ["warrior", "dreamer"]],
  [["king", "fool"], ["lover", "dreamer"], ["schemer", "warrior"]],
  [["king", "schemer"], ["lover", "warrior"], ["fool", "dreamer"]],
  [["king", "dreamer"], ["lover", "schemer"], ["fool", "warrior"]],
  [["king", "warrior"], ["lover", "fool"], ["schemer", "dreamer"]],
  // ----- Q7〜12: ひとりのあなた -----
  [["king", "lover"], ["fool", "schemer"], ["warrior", "dreamer"]],
  [["king", "fool"], ["lover", "dreamer"], ["schemer", "warrior"]],
  [["king", "schemer"], ["lover", "warrior"], ["fool", "dreamer"]],
  [["king", "dreamer"], ["lover", "schemer"], ["fool", "warrior"]],
  [["king", "warrior"], ["fool", "lover"], ["schemer", "dreamer"]],
  [["king", "lover"], ["fool", "schemer"], ["warrior", "dreamer"]],
];

export const QUESTION_COUNT = QUESTION_TYPES.length;
export const FRONT_QUESTIONS = 6;

/** 6問ぶんの答え (0〜2) からタイプを決める。同点なら、後に答えた問いで点が入ったタイプを優先 */
function decideType(qs: [TypeId, TypeId][][], answers: number[]): TypeId {
  const score = new Map<TypeId, number>();
  const last = new Map<TypeId, number>();
  qs.forEach((choices, i) => {
    for (const t of choices[answers[i]]) {
      score.set(t, (score.get(t) ?? 0) + 1);
      last.set(t, i);
    }
  });
  const top = Math.max(...score.values());
  const tied = [...score.keys()].filter((t) => score.get(t) === top);
  return tied.reduce((a, b) => (last.get(b)! > last.get(a)! ? b : a));
}

export interface Diagnosis {
  front: TypeId;
  back: TypeId;
  roleId: string;
}

export const isValidAnswers = (value: unknown): value is number[] =>
  Array.isArray(value) &&
  value.length === QUESTION_COUNT &&
  value.every((a) => a === 0 || a === 1 || a === 2);

export function diagnose(answers: number[]): Diagnosis {
  const front = decideType(QUESTION_TYPES.slice(0, FRONT_QUESTIONS), answers.slice(0, FRONT_QUESTIONS));
  const back = decideType(QUESTION_TYPES.slice(FRONT_QUESTIONS), answers.slice(FRONT_QUESTIONS));
  return { front, back, roleId: castingTable[front][back] };
}

// ===== 36人 =====

export interface RoleBase {
  id: string;
  front: TypeId;
  back: TypeId;
  /**
   * 相性のいい共演者 (36人の中のだれか)。
   * 18組の「お互いが共演者」のペアになっていて、36人全員がだれかの共演者として登場する
   */
  costar: string;
  /** 名台詞の原文 (シェイクスピアの英語)。どの言語でもそのまま見せ、下に各言語のわかりやすい訳を添える */
  quote: string;
}

export const ROLES: RoleBase[] = [
  { id: "henry5", front: "king", back: "king", costar: "falstaff", quote: "Once more unto the breach, dear friends, once more;" },
  { id: "cleopatra", front: "king", back: "lover", costar: "bastard", quote: "I have immortal longings in me." },
  { id: "lear", front: "king", back: "fool", costar: "marina", quote: "Who is it that can tell me who I am?" },
  { id: "margaret", front: "king", back: "schemer", costar: "richard3", quote: "Great lords, wise men ne'er sit and wail their loss, But cheerly seek how to redress their harms." },
  { id: "katherine", front: "king", back: "warrior", costar: "coriolanus", quote: "Sir, I desire you do me right and justice;" },
  { id: "richard2", front: "king", back: "dreamer", costar: "cressida", quote: "For God's sake, let us sit upon the ground And tell sad stories of the death of kings;" },
  { id: "hermione", front: "lover", back: "king", costar: "timon", quote: "I am not prone to weeping, as our sex Commonly are;" },
  { id: "juliet", front: "lover", back: "lover", costar: "imogen", quote: "What's in a name? That which we call a rose By any other name would smell as sweet;" },
  { id: "launce", front: "lover", back: "fool", costar: "dromio", quote: "I think Crab, my dog, be the sourest-natured dog that lives:" },
  { id: "helena", front: "lover", back: "schemer", costar: "jackcade", quote: "Our remedies oft in ourselves do lie, Which we ascribe to heaven:" },
  { id: "imogen", front: "lover", back: "warrior", costar: "juliet", quote: "I see before me, man: nor here, nor here, Nor what ensues, but have a fog in them, That I cannot look through." },
  { id: "cressida", front: "lover", back: "dreamer", costar: "richard2", quote: "Men prize the thing ungain'd more than it is:" },
  { id: "jackcade", front: "fool", back: "king", costar: "helena", quote: "Then are we in order when we are most out of order." },
  { id: "berowne", front: "fool", back: "lover", costar: "beatrice", quote: "They are the books, the arts, the academes, That show, contain and nourish all the world:" },
  { id: "dromio", front: "fool", back: "fool", costar: "launce", quote: "Am I Dromio? am I your man? am I myself?" },
  { id: "richard3", front: "fool", back: "schemer", costar: "margaret", quote: "Now is the winter of our discontent Made glorious summer by this sun of York;" },
  { id: "bastard", front: "fool", back: "warrior", costar: "cleopatra", quote: "This England never did, nor never shall, Lie at the proud foot of a conqueror," },
  { id: "falstaff", front: "fool", back: "dreamer", costar: "henry5", quote: "I am not only witty in myself, but the cause that wit is in other men." },
  { id: "shylock", front: "schemer", back: "king", costar: "titus", quote: "If you prick us, do we not bleed?" },
  { id: "rosalind", front: "schemer", back: "lover", costar: "viola", quote: "Men have died from time to time, and worms have eaten them, but not for love." },
  { id: "puck", front: "schemer", back: "fool", costar: "hamlet", quote: "Lord, what fools these mortals be!" },
  { id: "iago", front: "schemer", back: "schemer", costar: "mrsford", quote: "I am not what I am." },
  { id: "mrsford", front: "schemer", back: "warrior", costar: "iago", quote: "I will find you twenty lascivious turtles ere one chaste man." },
  { id: "viola", front: "schemer", back: "dreamer", costar: "rosalind", quote: "O time! thou must untangle this, not I; It is too hard a knot for me to untie!" },
  { id: "coriolanus", front: "warrior", back: "king", costar: "katherine", quote: "There is a world elsewhere." },
  { id: "katherina", front: "warrior", back: "lover", costar: "hotspur", quote: "If I be waspish, best beware my sting." },
  { id: "beatrice", front: "warrior", back: "fool", costar: "berowne", quote: "I had rather hear my dog bark at a crow than a man swear he loves me." },
  { id: "titus", front: "warrior", back: "schemer", costar: "shylock", quote: "Why, foolish Lucius, dost thou not perceive That Rome is but a wilderness of tigers?" },
  { id: "hotspur", front: "warrior", back: "warrior", costar: "katherina", quote: "O, the blood more stirs To rouse a lion than to start a hare!" },
  { id: "macbeth", front: "warrior", back: "dreamer", costar: "joan", quote: "Is this a dagger which I see before me, The handle toward my hand?" },
  { id: "brutus", front: "dreamer", back: "king", costar: "isabella", quote: "There is a tide in the affairs of men, Which, taken at the flood, leads on to fortune;" },
  { id: "marina", front: "dreamer", back: "lover", costar: "lear", quote: "I never spake bad word, nor did ill turn To any living creature:" },
  { id: "hamlet", front: "dreamer", back: "fool", costar: "puck", quote: "To be, or not to be: that is the question:" },
  { id: "isabella", front: "dreamer", back: "schemer", costar: "brutus", quote: "O, it is excellent To have a giant's strength; but it is tyrannous To use it like a giant." },
  { id: "joan", front: "dreamer", back: "warrior", costar: "macbeth", quote: "Glory is like a circle in the water, Which never ceaseth to enlarge itself Till by broad spreading it disperse to nought." },
  { id: "timon", front: "dreamer", back: "dreamer", costar: "hermione", quote: "I am Misanthropos, and hate mankind." },
];

/** 人前のタイプ × ひとりのタイプ → 役 */
export const castingTable = Object.fromEntries(
  typeOrder.map((f) => [f, Object.fromEntries(typeOrder.map((b) => [b, ""]))]),
) as Record<TypeId, Record<TypeId, string>>;
for (const r of ROLES) castingTable[r.front][r.back] = r.id;

export const roleBaseById = (id: string) => ROLES.find((r) => r.id === id);

/** 案内役 (37人目) プロスペローの名台詞の原文 */
export const PROSPERO_QUOTE = "We are such stuff As dreams are made on, and our little life Is rounded with a sleep.";

// ===== 会員 =====

/** この日時 (日本時間 2027年1月31日 23:59) までに登録した人が「創立メンバー」 */
export const FOUNDING_DEADLINE = "2027-01-31T14:59:59Z";
export const isFoundingMember = (createdAt: string) =>
  new Date(createdAt.replace(" ", "T") + (createdAt.endsWith("Z") ? "" : "Z")) <= new Date(FOUNDING_DEADLINE);
